import { useEffect, useMemo, useRef, useState } from "react";
import { Battery, Camera, Database, Radio } from "lucide-react";
import FieldLayout, {
  FieldErrors,
  FieldMetric,
} from "components/field/FieldLayout";
import FieldDiagnostics from "components/field/FieldDiagnostics";
import {
  BatteryBanner,
  BatteryCard,
  usePower,
} from "components/field/BatteryRunway";
import NumberDraft from "components/field/NumberDraft";
import DebugProfilerCard from "components/field/DebugProfilerCard";
import FirmwarePanel from "components/field/FirmwarePanel";
import LivePanel from "components/field/LivePanel";
import RadarRichDataCard from "components/field/RadarRichDataCard";
import { More } from "components/field/radarUi";
import RadarSensorSettings from "components/field/RadarSensorSettings";
import RichDataCosts from "components/field/RichDataCosts";
import SatellitePasses from "components/field/SatellitePasses";
import StoragePanel from "components/field/StoragePanel";
import { useAuth } from "context/AuthContext";
import { useFieldNodes, useFieldResource } from "hooks/useFieldNodes";
import { rtctApi } from "services/rtctApi";
import { fieldTime, fieldValue, needsFastRefresh } from "utils/fieldFormat";
import { richPlanSettings } from "utils/radarAnalysis";
import { BURST_RULES, uploadCadence } from "utils/radarBurst";
import { onCellular, timeOf } from "utils/radarDiagnostics";
import { EDGE_PLAN_KEYS, EDGE_RULES, readSuggestedGate } from "utils/radarEdge";
import { RICH_RULES } from "utils/radarRich";
// The node's rich readings of the last day as the server holds them, beside
// the counters in Radar rich data, which trail them.
const richDayLoader = (mac, signal) =>
  rtctApi.radarAnalysis(mac, signal, { days: 1, kind: "rich" });
const schedules = [
  [
    "interval_sec",
    "Wi-Fi measurement interval",
    "Seconds between measurements on Wi-Fi.",
    300,
  ],
  [
    "lte_interval_s",
    "Cellular measurement interval",
    "Seconds between measurements on cellular.",
    3600,
  ],
  [
    "send_every_s",
    "Wi-Fi send interval",
    "0 sends every wake; otherwise seconds between uploads.",
    0,
  ],
  [
    "lte_send_every_s",
    "Cellular send interval",
    "0 sends every wake; otherwise seconds between uploads.",
    0,
  ],
  [
    "cam_every_n_wakes",
    "Wi-Fi photo frequency",
    "Capture once every this many wakes.",
    1,
  ],
  [
    "lte_cam_every_h",
    "Cellular photo interval",
    "Hours between photos; 0 disables the cellular schedule.",
    6,
  ],
  [
    "gnss_every_n_wakes",
    "GNSS fix frequency",
    "Attempt a location fix once every this many wakes.",
    1,
  ],
  [
    "gnss_budget_s",
    "GNSS wake budget",
    "Maximum seconds allowed for a location fix.",
    60,
  ],
  [
    "radar_polls",
    "Radar polls per measurement",
    "Additional polls can improve confidence but increase awake time.",
    3,
  ],
  [
    "radar_agree_mm",
    "Radar agreement tolerance",
    "Maximum difference between agreeing measurements, in millimetres.",
    10,
  ],
];
const estimateFields = [
  [
    "battery_capacity_mah",
    "Nominal battery capacity (mAh)",
    100,
    100000,
    10000,
  ],
  ["active_current_ma", "Active node current (mA)", 1, 3000, 150],
  ["active_seconds", "Active time per measurement (s)", 0.1, 300, 20],
  ["sleep_current_ma", "Sleep current (mA)", 0, 100, 0.2],
  ["camera_current_ma", "Camera current (mA)", 0, 3000, 300],
  ["camera_seconds", "Time per paired capture (s)", 0, 120, 12],
  ["image_bytes", "JPEG size per camera (bytes)", 10000, 2097152, 250000],
];
const initialAssumptions = () =>
  Object.fromEntries(
    estimateFields
      .filter(([key]) => key !== "image_bytes")
      .map(([key, label, min, max, val]) => [key, val]),
  );
// A request's status for its chip: "Confirmed", "Not applied".
const statusText = (status) =>
  status
    ? `${status.charAt(0).toUpperCase()}${status.slice(1)}`.replaceAll("_", " ")
    : null;
const range = (r, unit) =>
  r && Number.isFinite(r.low) && Number.isFinite(r.high)
    ? `${r.low.toLocaleString(undefined, { maximumFractionDigits: 1 })}–${r.high.toLocaleString(undefined, { maximumFractionDigits: 1 })} ${unit}`
    : "Not estimated";

export default function NodeControl() {
  const nodes = useFieldNodes();
  return <NodeControlContent key={nodes.mac} nodes={nodes} />;
}
function NodeControlContent({ nodes }) {
  const [refresh, setRefresh] = useState(0);
  const auth = useAuth();
  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") setRefresh((n) => n + 1);
    }, 30000);
    return () => clearInterval(timer);
  }, []);
  const telemetry = useFieldResource(nodes.mac, rtctApi.telemetry, refresh),
    settings = useFieldResource(nodes.mac, rtctApi.settings, refresh);
  // Battery runway: its own 5-minute poll (the answer moves in days).
  const power = usePower(nodes.mac);
  const [storage, setStorage] = useState(null),
    [storageError, setStorageError] = useState("");
  const [draft, setDraft] = useState({}),
    [edits, setEdits] = useState({}),
    [busy, setBusy] = useState(false),
    [actionError, setActionError] = useState(""),
    [result, setResult] = useState(null),
    [projection, setProjection] = useState(null),
    [projectionError, setProjectionError] = useState(""),
    // The projection at the node's own estimate and raw-upload settings,
    // while the card plans others: Raw uploads compares the two.
    [projectionNow, setProjectionNow] = useState(null),
    [projectionNowFailed, setProjectionNowFailed] = useState(false),
    [assumptions, setAssumptions] = useState(initialAssumptions),
    [pickedTransport, setTransport] = useState(null),
    [live, setLive] = useState(null),
    [richPlan, setRichPlan] = useState(null),
    [liveHours, setLiveHours] = useState(null);
  const currentMac = useRef(nodes.mac);
  currentMac.current = nodes.mac;
  const scope = useRef({ mac: nodes.mac, generation: 0 });
  if (scope.current.mac !== nodes.mac)
    scope.current = {
      mac: nodes.mac,
      generation: scope.current.generation + 1,
    };
  const activeSettings = result || settings.data;
  const isStaff = Boolean(auth?.user?.is_staff);
  // Before you apply estimates the node's own network until another is
  // chosen: its newest reading's radio (Wi-Fi when there is none).
  const newestSample = useMemo(
    () =>
      (telemetry.data?.samples || []).reduce(
        (best, s) =>
          (timeOf(s?.timestamp) ?? -Infinity) >
          (timeOf(best?.timestamp) ?? -Infinity)
            ? s
            : best,
        null,
      ),
    [telemetry.data],
  );
  const nodeTransport = newestSample
    ? onCellular(newestSample.raw)
      ? "cellular"
      : "wifi"
    : null;
  const transport = pickedTransport ?? nodeTransport ?? "wifi";
  const richDay = useFieldResource(
    activeSettings?.allowed_keys?.includes("radar_rich") ? nodes.mac : "",
    richDayLoader,
    refresh,
  );
  // Firmware .60's radar link: radar/'s latest carries the newest reading's
  // search for the radar's rate and switch outcome (baud, baud_set).
  const radarView = useFieldResource(
    activeSettings?.allowed_keys?.includes("radar_baud") ||
      activeSettings?.allowed_keys?.includes("radar_edge")
      ? nodes.mac
      : "",
    rtctApi.radar,
    refresh,
  );
  // The node's own estimate (firmware .61-.62): the gate radar/profile/
  // suggests, so that the node's echoes are those the server considers.
  const edgeAllowed = !!activeSettings?.allowed_keys?.includes("radar_edge");
  const profileView = useFieldResource(
    edgeAllowed ? nodes.mac : "",
    rtctApi.radarProfile,
    refresh,
  );
  const suggestedGate = profileView.data
    ? readSuggestedGate(profileView.data)
    : profileView.error
      ? null
      : undefined;
  useEffect(() => {
    const abort = new AbortController();
    rtctApi
      .storage(abort.signal)
      .then((d) => {
        if (!abort.signal.aborted) {
          setStorage(d);
          setStorageError("");
        }
      })
      .catch((e) => {
        if (!abort.signal.aborted) setStorageError(e.message);
      });
    return () => abort.abort();
  }, [refresh]);
  useEffect(() => {
    setDraft({});
    setEdits({});
    setResult(null);
    setActionError("");
    setProjection(null);
    setBusy(false);
  }, [nodes.mac]);
  useEffect(() => {
    if (settings.data) {
      setDraft(settings.data.reported || {});
      setResult(null);
    }
  }, [settings.data]);
  // A link to a panel here (Radar analysis links to Live mode and to the
  // on-node estimate) scrolls to it, once it is there: the radar sections
  // show once the settings have arrived.
  const scrolled = useRef(false);
  useEffect(() => {
    if (scrolled.current) return;
    const id = decodeURIComponent(window.location.hash.slice(1));
    const target = id ? document.getElementById(id) : null;
    if (!id || target) scrolled.current = true;
    target?.scrollIntoView?.({ block: "start" });
  }, [activeSettings]);
  const preview = useMemo(() => ({ ...draft, ...edits }), [draft, edits]);
  // Rich data at the scenario's upload cadence: bursts come on every Nth
  // upload; rich readings on every wake.
  const uploadSeconds = useMemo(
    () =>
      uploadCadence(preview, [], { cellular: transport === "cellular" })
        .seconds,
    [preview, transport],
  );
  const wakeSeconds = useMemo(
    () =>
      uploadCadence(preview, [], { cellular: transport === "cellular" }).wake,
    [preview, transport],
  );
  // The radar keys as the projection checks them: whole numbers within the
  // node's rules (the firmware's for bursts and rich readings when it sends
  // none).
  const rules = activeSettings?.rules;
  const richSettings = useMemo(
    () =>
      richPlanSettings(
        richPlan,
        {
          ...BURST_RULES,
          ...RICH_RULES,
          ...EDGE_RULES,
          ...(rules || {}),
        },
        activeSettings?.set_keys,
      ),
    [richPlan, rules, activeSettings?.set_keys],
  );
  const estimateInput = useMemo(() => {
    const interval = Number(
      preview[transport === "wifi" ? "interval_sec" : "lte_interval_s"] ||
        schedules[transport === "wifi" ? 0 : 1][3],
    );
    const photo =
      transport === "wifi"
        ? interval * Number(preview.cam_every_n_wakes || 1)
        : Number(preview.lte_cam_every_h ?? 6) * 3600;
    // The upload interval of the transport estimated (0 = every wake), for
    // the bursts, which come on wakes that upload; left out when the node
    // reports none, so the server takes the node's.
    const send = Number(
      preview[transport === "wifi" ? "send_every_s" : "lte_send_every_s"] ??
        NaN,
    );
    return {
      ...assumptions,
      interval_sec: interval,
      photo_interval_sec: Math.max(60, Math.min(604800, photo)),
      retention_days: storage?.retention_days || 30,
      camera_enabled:
        preview.cam_enable !== 0 &&
        (transport === "wifi" || preview.lte_cam_every_h !== 0),
      // Radar rich data as set in its card (planned, else running): the
      // projection returns its battery and data lines, in its totals.
      ...richSettings,
      transport: transport === "wifi" ? "wifi" : "lte",
      ...(Number.isFinite(send) && send >= 0 && send <= 86400
        ? { send_every_s: send }
        : {}),
      ...(liveHours !== null ? { live_hours_per_day: liveHours } : {}),
    };
  }, [preview, transport, assumptions, storage, richSettings, liveHours]);
  // While the card plans another estimate or raw uploads: the same scenario
  // without them, which the server takes at the node's own.
  const edgePlanned = !!richPlan?.planned?.edge;
  const baselineInput = useMemo(
    () =>
      edgePlanned
        ? Object.fromEntries(
            Object.entries(estimateInput).filter(
              ([key]) => !EDGE_PLAN_KEYS.includes(key),
            ),
          )
        : null,
    [edgePlanned, estimateInput],
  );
  useEffect(() => {
    setProjectionNow(null);
    setProjectionNowFailed(false);
    if (!nodes.mac || !baselineInput) return;
    const abort = new AbortController();
    const timer = setTimeout(
      () =>
        rtctApi
          .projection(nodes.mac, baselineInput, abort.signal)
          .then((d) => {
            if (!abort.signal.aborted) setProjectionNow(d);
          })
          .catch(() => {
            if (!abort.signal.aborted) setProjectionNowFailed(true);
          }),
      300,
    );
    return () => {
      clearTimeout(timer);
      abort.abort();
    };
  }, [nodes.mac, baselineInput]);
  useEffect(() => {
    if (!nodes.mac) return;
    const abort = new AbortController();
    setProjection(null);
    setProjectionError("");
    const timer = setTimeout(
      () =>
        rtctApi
          .projection(nodes.mac, estimateInput, abort.signal)
          .then((d) => {
            if (!abort.signal.aborted) setProjection(d);
          })
          .catch((e) => {
            if (!abort.signal.aborted) setProjectionError(e.message);
          }),
      300,
    );
    return () => {
      clearTimeout(timer);
      abort.abort();
    };
  }, [nodes.mac, estimateInput]);
  const change = (key, value) => {
    setEdits((old) => ({ ...old, [key]: value }));
    setActionError("");
  };
  const apply = async () => {
    const mac = nodes.mac,
      generation = scope.current.generation;
    setBusy(true);
    setActionError("");
    try {
      const data = await rtctApi.applySettings(
        mac,
        Object.fromEntries(
          Object.entries(edits).filter(([key]) =>
            activeSettings.allowed_keys.includes(key),
          ),
        ),
      );
      if (currentMac.current !== mac || scope.current.generation !== generation)
        return;
      setResult(data);
      setEdits({});
    } catch (e) {
      if (currentMac.current === mac && scope.current.generation === generation)
        setActionError(e.message);
    } finally {
      if (currentMac.current === mac && scope.current.generation === generation)
        setBusy(false);
    }
  };
  useEffect(() => {
    if (!needsFastRefresh(activeSettings)) return;
    const timer = setTimeout(() => setRefresh((n) => n + 1), 15000);
    return () => clearTimeout(timer);
  }, [activeSettings]);
  const invalidSend = Object.entries(edits).some(
    ([key, value]) => key.includes("send_every") && value > 0 && value < 60,
  );
  const allowed = activeSettings?.allowed_keys || [];
  const reported = activeSettings?.reported || {},
    desired = activeSettings?.desired || {};
  // Requested versus running: every setting, and those the node has not
  // taken up yet (or that are edited here), which keep the table open.
  const settingKeys = Array.from(
    new Set([
      ...Object.keys(desired),
      ...Object.keys(reported),
      ...Object.keys(edits),
    ]),
  );
  const waitingKeys = Object.keys(desired).filter(
    (key) => fieldValue(desired[key]) !== fieldValue(reported[key]),
  );
  // A request on its way, or one the node has answered (its differences then
  // stay as they are).
  const onItsWay = ["queued", "sent", "acknowledged"].includes(
    activeSettings?.status,
  );
  const editedCount = Object.keys(edits).length;
  return (
    <FieldLayout
      nodes={nodes}
      title="Node control"
      subtitle="Settings, live mode and firmware updates."
      section="control"
      refresh={() => setRefresh((n) => n + 1)}
    >
      <FieldErrors errors={[settings.error, telemetry.error, actionError]} />
      <BatteryBanner power={power} />
      <LivePanel mac={nodes.mac} storage={storage} onState={setLive} />
      <div className="field-control-grid">
        <div className="field-control-column">
          <section className="field-panel">
            <div className="field-panel-head">
              <div>
                <h2>Measurement & capture schedules</h2>
                <p>Local preview · changes send only when you apply</p>
              </div>
              <span className="field-state">
                {Object.keys(edits).length
                  ? "Unsaved preview"
                  : statusText(activeSettings?.status) || "Loading"}
              </span>
            </div>
            <div className="field-panel-body">
              {schedules.map(([key, label, help, defaultValue]) => {
                const rules = activeSettings?.rules?.[key] || [0, 86400];
                const value = preview[key] ?? defaultValue;
                const supported = allowed.includes(key);
                return (
                  <div className="field-setting" key={key}>
                    <label htmlFor={`setting-${key}`}>
                      {label}
                      <small>
                        {help}{" "}
                        {reported[key] === undefined
                          ? "No device read-back yet."
                          : `Reported: ${reported[key]}.`}
                      </small>
                    </label>
                    <NumberDraft
                      id={`setting-${key}`}
                      aria-label={label}
                      min={rules[0]}
                      max={rules[1]}
                      step={1}
                      value={value}
                      disabled={!supported || busy}
                      onChange={(v) => change(key, Math.round(v))}
                    />
                    <input
                      aria-label={`${label} slider`}
                      type="range"
                      min={rules[0]}
                      max={rules[1]}
                      step={1}
                      value={value}
                      disabled={!supported || busy}
                      onChange={(e) => change(key, Number(e.target.value))}
                    />
                    {!supported && (
                      <small>Unavailable on this API or firmware.</small>
                    )}
                  </div>
                );
              })}
              <div className="field-checks" style={{ margin: "22px 0" }}>
                {[
                  ["cam_enable", "Enable cameras"],
                  ["cam_night_skip", "Skip night captures"],
                  ["gnss_on_wifi", "GNSS on Wi-Fi"],
                ].map(([k, label]) => (
                  <label key={k}>
                    <input
                      type="checkbox"
                      checked={preview[k] === 1}
                      disabled={!allowed.includes(k) || busy}
                      onChange={(e) => change(k, e.target.checked ? 1 : 0)}
                    />
                    {label}
                  </label>
                ))}
              </div>
              {invalidSend && (
                <div className="field-alert" role="alert">
                  Send intervals must be 0 or at least 60 seconds.
                </div>
              )}
              <div className="field-notice">
                Requests stay pending until the device wakes and reports its
                running settings. A successful publish confirms command
                submission; device read-back confirms the change.
              </div>
              <div className="field-actions">
                <button
                  className="field-button primary"
                  disabled={busy || invalidSend || !Object.keys(edits).length}
                  onClick={apply}
                >
                  {busy
                    ? "Sending request…"
                    : `Apply ${Object.keys(edits).length || ""} ${Object.keys(edits).length === 1 ? "change" : "changes"}`}
                </button>
                <button
                  className="field-button"
                  disabled={busy || !Object.keys(edits).length}
                  onClick={() => setEdits({})}
                >
                  Discard preview
                </button>
              </div>
              <p className="field-caption">
                Only fields you edit are sent. GNSS on Wi-Fi and fix budgets are
                available when supported. Cellular power switching is
                unavailable in the current firmware settings interface.
              </p>
            </div>
          </section>
          <RadarRichDataCard
            key={`rich-${nodes.mac}`}
            mac={nodes.mac}
            live={live}
            settings={activeSettings}
            samples={telemetry.data?.samples || []}
            onSent={() => setRefresh((n) => n + 1)}
            onPlan={setRichPlan}
            projection={projection}
            projectionFailed={!!projectionError}
            transport={transport}
            nodeTransport={nodeTransport}
            richDay={richDay.data}
            radarLatest={radarView.data?.latest ?? null}
            projectionNow={projectionNow}
            projectionNowFailed={projectionNowFailed}
            suggestedGate={suggestedGate}
          />
        </div>
        <div className="field-control-column">
          <section className="field-panel">
            <div className="field-panel-head">
              <div>
                <h2>Before you apply</h2>
                <p>
                  Scenario estimates · {projection?.confidence || "calculating"}{" "}
                  · solar gain excluded
                  {nodeTransport
                    ? ` · the node runs on ${nodeTransport === "cellular" ? "cellular" : "Wi-Fi"}`
                    : ""}
                </p>
              </div>
              <div className="field-segment">
                {["wifi", "cellular"].map((mode) => (
                  <button
                    key={mode}
                    className={transport === mode ? "active" : ""}
                    aria-pressed={transport === mode}
                    onClick={() => setTransport(mode)}
                  >
                    {mode === "wifi" ? "Wi-Fi" : "Cellular"}
                  </button>
                ))}
              </div>
            </div>
            <div className="field-panel-body">
              {projectionError && (
                <div className="field-alert">{projectionError}</div>
              )}
              <div
                className="field-metrics"
                style={{ gridTemplateColumns: "1fr 1fr", margin: "0 0 16px" }}
              >
                <FieldMetric
                  icon={Battery}
                  label="Projected battery life"
                  value={range(projection?.battery_days, "days")}
                  detail="At assumed usable capacity; not a cutoff date"
                />
                <FieldMetric
                  icon={Radio}
                  label="Data transferred"
                  value={range(projection?.data_mb_day, "MB/day")}
                  detail="Includes protocol and retry overhead"
                />
                <FieldMetric
                  icon={Database}
                  label="Projected storage growth"
                  value={range(projection?.storage_mb_day, "MB/day")}
                  detail="Per node · originals, thumbnails and telemetry"
                />
                <FieldMetric
                  icon={Camera}
                  label="Thirty-day data budget"
                  value={
                    projection
                      ? range(
                          {
                            low: projection.data_mb_day.low * 30,
                            high: projection.data_mb_day.high * 30,
                          },
                          "MB",
                        )
                      : "Not estimated"
                  }
                  detail="Continuous operation under this scenario"
                />
              </div>
              <RichDataCosts
                key={nodes.mac}
                projection={projection}
                plan={richPlan}
                uploadSeconds={uploadSeconds}
                wakeSeconds={wakeSeconds}
                cellular={transport === "cellular"}
                failed={!!projectionError}
                liveHours={liveHours}
                onLiveHours={setLiveHours}
              />
              <p className="field-caption">
                Preview: measurements every {estimateInput.interval_sec}{" "}
                seconds;{" "}
                {estimateInput.camera_enabled
                  ? `paired photos every ${estimateInput.photo_interval_sec} seconds`
                  : "camera schedule disabled"}
                . Missing reported settings use scenario defaults. Transport
                selection models a network; it does not switch the node’s
                network.
              </p>
              <details style={{ marginTop: 16 }}>
                <summary
                  style={{ cursor: "pointer", fontSize: 12, fontWeight: 700 }}
                >
                  Edit assumptions & see calculation limits
                </summary>
                <div style={{ marginTop: 14 }}>
                  {estimateFields.map(([key, label, min, max]) => (
                    <div className="field-control-row" key={key}>
                      <label htmlFor={`assumption-${key}`}>
                        {label}
                        <NumberDraft
                          id={`assumption-${key}`}
                          min={min}
                          max={max}
                          step="any"
                          value={
                            assumptions[key] ??
                            projection?.image_bytes ??
                            250000
                          }
                          onChange={(v) =>
                            setAssumptions((old) => ({ ...old, [key]: v }))
                          }
                        />
                      </label>
                    </div>
                  ))}
                  <p className="field-caption">
                    JPEG-size input:{" "}
                    {assumptions.image_bytes !== undefined
                      ? "operator scenario"
                      : projection?.image_size_source === "recent_mean"
                        ? "measured recent mean for this node"
                        : "assumed until camera uploads arrive"}{" "}
                    ·{" "}
                    {Math.round(
                      assumptions.image_bytes ??
                        projection?.image_bytes ??
                        250000,
                    ).toLocaleString()}{" "}
                    bytes per camera.
                    {assumptions.image_bytes !== undefined && (
                      <button
                        className="field-button"
                        style={{ marginLeft: 8 }}
                        onClick={() =>
                          setAssumptions((old) => {
                            const next = { ...old };
                            delete next.image_bytes;
                            return next;
                          })
                        }
                      >
                        Use measured size
                      </button>
                    )}
                  </p>
                  {projection?.assumptions?.map((a) => (
                    <p className="field-caption" key={a}>
                      {a}
                    </p>
                  ))}
                  {projection?.warnings?.map((a) => (
                    <p className="field-caption" key={a}>
                      {a}
                    </p>
                  ))}
                  <p className="field-caption">
                    Electrical assumptions are editable planning inputs, not
                    measured consumption. Battery age, temperature, poor signal
                    and retries can reduce operating time.
                  </p>
                </div>
              </details>
            </div>
          </section>
          <StoragePanel data={storage} error={storageError} />
          <BatteryCard power={power} />
          {isStaff && (
            <DebugProfilerCard mac={nodes.mac} settings={activeSettings} onSent={() => setRefresh((n) => n + 1)} />
          )}
          <SatellitePasses mac={nodes.mac} />
        </div>
      </div>
      <section className="field-panel" style={{ marginTop: 22 }}>
        <div className="field-panel-head">
          <div>
            <h2>Requested versus running</h2>
            <p>
              {activeSettings?.updated_at
                ? `Request updated ${fieldTime(activeSettings.updated_at)} SGT`
                : "No settings request yet"}
              {activeSettings?.reason ? ` · ${activeSettings.reason}` : ""}
            </p>
          </div>
          <span className="field-state">
            {statusText(activeSettings?.status) || "Awaiting report"}
          </span>
        </div>
        <div className="field-panel-body">
          {settingKeys.length > 0 && (
            <p className="field-requested-summary">
              {settingKeys.length} setting{settingKeys.length === 1 ? "" : "s"}
              {" · "}
              {!Object.keys(desired).length
                ? "nothing requested yet"
                : waitingKeys.length
                  ? `${waitingKeys.length} ${onItsWay ? "still to reach the node" : "not as requested on the node"} (${waitingKeys.join(", ")})`
                  : "the node runs every value requested"}
              {editedCount ? ` · ${editedCount} edited here, not sent yet` : ""}
            </p>
          )}
          <More
            label={`All settings, requested and reported${settingKeys.length ? ` (${settingKeys.length})` : ""}`}
            open={waitingKeys.length > 0 || editedCount > 0}
          >
            <div className="field-table-scroll">
              <table className="field-table">
                <thead>
                  <tr>
                    <th>Setting</th>
                    <th>Requested</th>
                    <th>Reported by device</th>
                    <th>Local preview</th>
                  </tr>
                </thead>
                <tbody>
                  {settingKeys.map((key) => (
                    <tr key={key}>
                      <td>{key}</td>
                      <td>{fieldValue(desired[key])}</td>
                      <td>{fieldValue(reported[key])}</td>
                      <td>
                        {Object.prototype.hasOwnProperty.call(edits, key)
                          ? fieldValue(edits[key])
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </More>
          {!Object.keys(desired).length && !Object.keys(reported).length && (
            <p className="field-caption">
              Waiting for the first device settings report. Scenario defaults do
              not represent confirmed running settings.
            </p>
          )}
        </div>
      </section>
      <FirmwarePanel
        key={nodes.mac}
        mac={nodes.mac}
        nodeName={nodes.node?.name || nodes.mac}
      />
      <RadarSensorSettings
        key={`sensor-${nodes.mac}`}
        mac={nodes.mac}
        settings={activeSettings}
        refresh={refresh}
      />
      <FieldDiagnostics
        samples={telemetry.data?.samples || []}
        history={false}
      />
    </FieldLayout>
  );
}
